import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import './IadeFaturaDetayModal.css';
import { formatApiDate } from '../utils/security';
import {
  iadeFaturaOnayla,
  iadeFaturaIptal,
  iadeFaturaFaturaGir,
} from '../services/iadeFaturaService';

const formatCurrency = (amount) => {
  if (amount == null) return '—';
  return `${parseFloat(amount).toFixed(2)} ₺`;
};

const DURUM_LABELS = {
  OnayBekliyor: 'Onay Bekliyor',
  FaturaBekleyen: 'Fatura Bekleyen',
  Tamamlandi: 'Tamamlandı',
  IptalEdildi: 'İptal Edildi',
};

const IadeFaturaDetayModal = ({ iadeFatura, onClose, onRefresh }) => {
  const { user } = useAuth();
  const isAdmin = ['admin', 'mod', 'moderator', 'sales'].includes(user?.rol);
  const isTechService = (user?.rol || '').toLowerCase() === 'tech-service';

  const [submitting, setSubmitting] = useState(false);
  const [faturaNumarasi, setFaturaNumarasi] = useState('');

  const d = iadeFatura;
  if (!d) return null;

  const durum = d.durum;
  const canOnayla = isAdmin && durum === 'OnayBekliyor';
  const canIptal = isAdmin && durum === 'OnayBekliyor';
  const canFaturaGir = isTechService && durum === 'FaturaBekleyen' && (parseFloat(d.toplamTutar) || 0) > 0;

  const handleOnayla = async () => {
    if (!d.id && !d._id) return;
    setSubmitting(true);
    try {
      const res = await iadeFaturaOnayla(d.id || d._id);
      if (res?.basarili) {
        onRefresh?.();
        window.showNotification?.('success', 'İade fatura onaylandı');
        onClose?.();
      } else {
        window.showNotification?.('error', res?.mesaj || 'Onaylanamadı');
      }
    } catch (e) {
      window.showNotification?.('error', e?.message || 'Onaylanamadı');
    } finally {
      setSubmitting(false);
    }
  };

  const handleIptal = async () => {
    if (!d.id && !d._id) return;
    if (!window.confirm('Bu iade fatura talebini iptal etmek istediğinize emin misiniz? Stok rezervasyonu geri alınır; cari ve iade kargo işlemleri yapılmaz.')) {
      return;
    }
    setSubmitting(true);
    try {
      const res = await iadeFaturaIptal(d.id || d._id);
      if (res?.basarili) {
        onRefresh?.();
        window.showNotification?.('success', 'İade fatura iptal edildi');
        onClose?.();
      } else {
        window.showNotification?.('error', res?.mesaj || 'İptal edilemedi');
      }
    } catch (e) {
      window.showNotification?.('error', e?.message || 'İptal edilemedi');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFaturaGir = async () => {
    const toplamTutar = parseFloat(d?.toplamTutar) || 0;
    if (toplamTutar > 0 && (!faturaNumarasi || !faturaNumarasi.trim())) {
      window.showNotification?.('error', 'Fatura numarası girin');
      return;
    }
    setSubmitting(true);
    try {
      const res = await iadeFaturaFaturaGir(d.id || d._id, faturaNumarasi.trim());
      if (res?.basarili) {
        onRefresh?.();
        setFaturaNumarasi('');
        window.showNotification?.('success', 'Fatura bilgisi eklendi');
        onClose?.();
      } else {
        window.showNotification?.('error', res?.mesaj || 'Eklenemedi');
      }
    } catch (e) {
      window.showNotification?.('error', e?.message || 'Eklenemedi');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content iade-fatura-detay-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>İade Fatura Detayı</h2>
          <button type="button" className="modal-close-btn" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>
        <div className="modal-body">
          <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontFamily: 'ui-monospace, monospace', fontWeight: 700, fontSize: '1.05rem', marginBottom: 6 }}>
                {d.iadeFaturaNumarasi || '—'}
              </div>
              <div><strong>Teknik Servis:</strong> {d.teknikServisAdi || '—'}</div>
              <div><strong>Tarih:</strong> {formatApiDate(d.olusturmaTarihi)}</div>
              {d.faturaNumarasi ? (
                <div><strong>Mali fatura no:</strong> {d.faturaNumarasi}</div>
              ) : null}
            </div>
            <span className="status-badge">{DURUM_LABELS[durum] || durum}</span>
          </div>

          <table className="admins-table">
            <thead>
              <tr>
                <th>Parça</th>
                <th style={{ textAlign: 'right' }}>Adet</th>
                <th style={{ textAlign: 'right' }}>Birim Fiyat</th>
                <th style={{ textAlign: 'right' }}>Toplam</th>
              </tr>
            </thead>
            <tbody>
              {(d.parcalar || []).map((p, idx) => (
                <tr key={`${idx}-${p.birimFiyat}-${p.adet}-${p.stokKatmanId || ''}`}>
                  <td>{p.parcaAdi || '—'} {p.parcaKodu ? `(${p.parcaKodu})` : ''}</td>
                  <td style={{ textAlign: 'right' }}>{p.adet}</td>
                  <td style={{ textAlign: 'right' }}>{formatCurrency(p.birimFiyat)}</td>
                  <td style={{ textAlign: 'right' }}>{formatCurrency(p.toplam)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div style={{ marginTop: 12, fontWeight: 600, textAlign: 'right' }}>
            TOPLAM: {formatCurrency(d.toplamTutar)}
          </div>

          {canFaturaGir && (
            <div style={{ marginTop: 16, padding: 12, background: 'var(--bg-light)', borderRadius: 8 }}>
              <label style={{ display: 'block', marginBottom: 6 }}>Fatura Numarası *</label>
              <input
                type="text"
                value={faturaNumarasi}
                onChange={(e) => setFaturaNumarasi(e.target.value)}
                placeholder={(d.toplamTutar == null || parseFloat(d.toplamTutar) === 0) ? 'Kullanılmamış iade için örn: K/I' : 'Fatura numarasını girin'}
                style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--border-color)', borderRadius: 6 }}
              />
            </div>
          )}

        </div>
        <div className="modal-footer">
          {canOnayla && (
            <button type="button" className="btn btn-primary" onClick={handleOnayla} disabled={submitting}>
              {submitting ? 'Onaylanıyor...' : 'Onayla'}
            </button>
          )}
          {canIptal && (
            <button type="button" className="btn btn-danger" onClick={handleIptal} disabled={submitting}>
              {submitting ? 'İptal ediliyor...' : 'İptal Et'}
            </button>
          )}
          {canFaturaGir && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleFaturaGir}
              disabled={submitting || ((parseFloat(d?.toplamTutar) || 0) > 0 && !faturaNumarasi?.trim())}
            >
              {submitting ? 'Kaydediliyor...' : 'Fatura Bilgisi Kaydet'}
            </button>
          )}
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Kapat
          </button>
        </div>
      </div>
    </div>
  );
};

export default IadeFaturaDetayModal;
